import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FolderZip as FolderZipIcon,
} from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UserVersionFilesManager from "../../components/admin/UserVersionFilesManager";
import VersionFormDialog from "../../components/admin/VersionFormDialog";
import { useAuth } from "../../contexts/AuthContext";
import {
  createProductVersion,
  deleteProductVersion,
  getProductById,
  getProductVersions,
  setVersionAsLatest,
  startDownload,
  updateProductVersion,
} from "../../services/productService";
import { userVersionFileService } from "../../services/userVersionFileService";

const ProductVersionsPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [zipDialogVersion, setZipDialogVersion] = useState(null);
  const [zipCounts, setZipCounts] = useState({});

  const isAdmin = user?.role === "admin";

  // Per-version registered/total counts for the badge
  const loadZipCounts = useCallback(async (versionList) => {
    try {
      const entries = await Promise.all(
        (versionList || []).map(async (v) => {
          const data = await userVersionFileService.getVersionStatus(v.id);
          const total = data.users.length;
          const registered = data.users.filter((u) => u.file).length;
          return [v.id, { registered, total }];
        }),
      );
      setZipCounts(Object.fromEntries(entries));
    } catch (err) {
      console.error("Error loading zip counts:", err);
    }
  }, []);

  useEffect(() => {
    fetchProductAndVersions();
  }, [productId]);

  const fetchProductAndVersions = async () => {
    try {
      setLoading(true);
      const productData = await getProductById(productId);
      setProduct(productData);

      const versionsData = await getProductVersions(productId);
      console.log("Versions fetched:", versionsData);

      // Ensure versions have proper IDs
      if (versionsData && versionsData.length > 0) {
        versionsData.forEach((version) => {
          console.log(
            `Version ID: ${version.id}, Version: ${version.version}, isLatest: ${version.is_latest}`,
          );
        });
      } else {
        console.log("No versions found for this product");
      }

      setVersions(versionsData);
      loadZipCounts(versionsData);

      setError("");
    } catch (err) {
      setError("Failed to fetch product versions. Please try again.");
      console.error("Error fetching product versions:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVersion = () => {
    setCurrentVersion(null);
    setOpenDialog(true);
  };

  const handleEditVersion = (version) => {
    setCurrentVersion(version);
    setOpenDialog(true);
  };

  const handleDeleteVersion = async (versionId) => {
    if (window.confirm("Are you sure you want to delete this version?")) {
      try {
        await deleteProductVersion(productId, versionId);
        setVersions(versions.filter((version) => version.id !== versionId));
        showSuccessMessage("Product version deleted successfully");
      } catch (err) {
        setError("Failed to delete version. Please try again.");
        console.error("Error deleting version:", err);
      }
    }
  };

  const handleSetLatest = async (versionId) => {
    try {
      await setVersionAsLatest(productId, versionId);
      // Update versions list to reflect the change
      setVersions(
        versions.map((version) => ({
          ...version,
          is_latest: version.id === versionId,
        })),
      );
      showSuccessMessage("Version set as latest successfully");
    } catch (err) {
      setError("Failed to set version as latest. Please try again.");
      console.error("Error setting version as latest:", err);
    }
  };

  const handleVersionSaved = async (versionData) => {
    try {
      const isNew = versionData.isNew;
      let savedVersion;

      // Close dialog first to prevent double submission
      setOpenDialog(false);

      if (isNew) {
        // Remove isNew and id from the data sent to backend for new versions
        const { isNew: _, id: __, ...dataToSend } = versionData;
        savedVersion = await createProductVersion(productId, dataToSend);
      } else {
        // Remove isNew from the data sent to backend
        const { isNew: _, ...dataToSend } = versionData;
        savedVersion = await updateProductVersion(
          productId,
          versionData.id,
          dataToSend,
        );
      }

      console.log("Version saved:", savedVersion, "isNew:", isNew);

      // After saving, fetch all versions again to ensure state is in sync with server
      await fetchProductAndVersions();

      // Show success message
      if (isNew) {
        showSuccessMessage("Product version added successfully");
      } else {
        showSuccessMessage("Product version updated successfully");
      }
    } catch (err) {
      console.error("Error saving version:", err);
      setError("Failed to save version. Please try again.");
      // Reopen dialog if there was an error
      setOpenDialog(true);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + " KB";
    else if (bytes < 1073741824) return (bytes / 1048576).toFixed(2) + " MB";
    else return (bytes / 1073741824).toFixed(2) + " GB";
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleBackToProducts = () => {
    navigate("/admin/products");
  };

  const handleDownload = async (versionId) => {
    try {
      const response = await startDownload(versionId);
      // Open download URL in new tab
      window.open(response.downloadUrl, "_blank");
    } catch (err) {
      setError("Failed to start download. Please try again.");
      console.error("Error starting download:", err);
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mr: { sm: 2 },
            mb: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
          size={window.innerWidth < 600 ? "small" : "medium"}
        >
          Back
        </Button>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h4" component="h1">
          {product?.name} - Versions
        </Typography>
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 3 }}>
        {product?.description || "No description available"}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddVersion}
        >
          Add Version
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>File Path</TableCell>
              <TableCell>File Size</TableCell>
              <TableCell>Changelog</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {versions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No versions found
                </TableCell>
              </TableRow>
            ) : (
              versions.map((version, index) => (
                <TableRow
                  key={version.id}
                  hover
                  sx={{
                    "&:hover": {
                      borderBottomColor: "primary.main",
                    },
                    "& td": {
                      py: 2,
                    },
                  }}
                >
                  <TableCell>{version.version}</TableCell>
                  <TableCell>{version.file_path}</TableCell>
                  <TableCell>{formatFileSize(version.file_size)}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor:
                          version.changelog && version.changelog.length > 50
                            ? "pointer"
                            : "default",
                      }}
                      title={version.changelog || "No changelog available"}
                    >
                      {version.changelog || "No changelog available"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {version.is_latest ? (
                      <Chip label="Latest" color="primary" />
                    ) : (
                      <Chip label="Old" color="default" />
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleEditVersion(version)}
                      aria-label="edit version"
                    >
                      <EditIcon />
                    </IconButton>
                    {!version.is_latest && (
                      <IconButton
                        color="success"
                        onClick={() => handleSetLatest(version.id)}
                        aria-label="set as latest"
                      >
                        <CheckIcon />
                      </IconButton>
                    )}
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteVersion(version.id)}
                      aria-label="delete version"
                    >
                      <DeleteIcon />
                    </IconButton>
                    {(zipCounts[version.id]?.total ?? 0) > 0 && (
                      <Tooltip title="License server zips">
                        <IconButton
                          size="small"
                          onClick={() => setZipDialogVersion(version)}
                        >
                          <Badge
                            badgeContent={`${zipCounts[version.id].registered}/${zipCounts[version.id].total}`}
                            color={
                              zipCounts[version.id].registered ===
                              zipCounts[version.id].total
                                ? "success"
                                : "warning"
                            }
                          >
                            <FolderZipIcon fontSize="small" />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <VersionFormDialog
        open={openDialog}
        productId={productId}
        version={currentVersion}
        onClose={() => setOpenDialog(false)}
        onSave={handleVersionSaved}
      />

      <UserVersionFilesManager
        open={Boolean(zipDialogVersion)}
        onClose={() => setZipDialogVersion(null)}
        mode="version"
        versionId={zipDialogVersion?.id}
        versionLabel={zipDialogVersion ? `v${zipDialogVersion.version}` : ""}
        productName={product?.name}
        onChanged={() => loadZipCounts(versions)}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductVersionsPage;
